import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit,
    Trash2,
    MapPin,
    Eye,
    ChevronLeft,
    Users,
    Activity,
    Target,
    LayoutGrid,
    MoreVertical,
    Send,
    UserCircle,
    Building2,
    Calendar,
    Phone,
    Mail,
    Loader2,
    Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import AddFieldWorkerModal from './AddFieldWorkerModal';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';

interface FieldWorkersProps {
    onBack: () => void;
}

const FieldWorkers: React.FC<FieldWorkersProps> = ({ onBack }) => {
    const [workers, setWorkers] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [statsData, setStatsData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedLocation, setSelectedLocation] = useState('all');

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState<any>(null);

    useEffect(() => {
        fetchData();
        fetchLocations();
        fetchStats();
    }, []);

    useEffect(() => {
        fetchData();
    }, [search, selectedStatus, selectedLocation]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams();
            if (search) query.append('q', search);
            if (selectedStatus !== 'all') query.append('status', selectedStatus);
            if (selectedLocation !== 'all') query.append('locationId', selectedLocation);

            const data = await api.get(`/api/inventory/workers?${query.toString()}`);
            setWorkers(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const locs = await api.get('/api/inventory/locations');
            setLocations(locs);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await api.get('/api/inventory/workers/stats');
            setStatsData(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await api.delete(`/api/inventory/workers/${id}`);
            toast.success('Worker deleted successfully');
            fetchData();
            fetchStats();
            setIsDeleteModalOpen(false);
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete worker');
        }
    };

    const stats = [
        { label: 'Total Workers', count: statsData?.totalWorkers || 0, icon: <Users size={20} className="text-blue-500" />, bg: 'bg-blue-50' },
        { label: 'Active Workers', count: (statsData?.totalWorkers - (statsData?.totalDrafts || 0)) || 0, icon: <Activity size={20} className="text-green-500" />, bg: 'bg-green-50' },
        { label: 'Locations', count: statsData?.locationsCount || 0, icon: <MapPin size={20} className="text-orange-500" />, bg: 'bg-orange-50' },
        { label: 'Recent Issuances', count: statsData?.recentIssuancesCount || 0, icon: <Send size={20} className="text-purple-500" />, bg: 'bg-purple-50' }
    ];

    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    return (
        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1.5 text-gray-400 hover:text-gray-900 transition-all p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-bold sm:inline hidden">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Field Workers</h1>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            Personnel Directory
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="hidden sm:flex flex-initial rounded-2xl h-11 px-6 border-gray-100 font-bold hover:bg-gray-50 transition-all shadow-sm text-xs items-center gap-2"
                    >
                        <LayoutGrid size={16} />
                        Import Workers
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => { setSelectedWorker(null); setIsAddModalOpen(true); }}
                        className="flex-1 sm:flex-initial items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-11 px-4 sm:px-6 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs sm:text-sm"
                    >
                        <Plus size={18} />
                        <span className="whitespace-nowrap">Add Worker</span>
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {stats.map((s, idx) => (
                    <div key={idx} className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4">
                        <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0", s.bg)}>
                            {s.icon}
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider leading-none mb-1 truncate">{s.label}</p>
                            <p className="text-lg sm:text-xl font-black text-gray-900">{s.count}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search & Filters */}
            <div className="bg-white p-4 sm:p-5 rounded-[32px] border border-gray-100 shadow-sm space-y-4">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={20} />
                    <Input
                        placeholder="Search by name, employee ID..."
                        className="pl-12 rounded-[20px] border-gray-100 bg-gray-50/50 h-12 transition-all focus:bg-white focus:ring-4 focus:ring-blue-500/5 font-medium text-sm border-none shadow-inner"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger className="w-full rounded-[18px] border-gray-100 bg-gray-50/50 h-11 font-bold text-xs hover:bg-white transition-all">
                            <Activity size={14} className="text-gray-400 mr-2" />
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                        <SelectTrigger className="w-full rounded-[18px] border-gray-100 bg-gray-50/50 h-11 font-bold text-xs hover:bg-white transition-all">
                            <MapPin size={14} className="text-gray-400 mr-2" />
                            <SelectValue placeholder="All Locations" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Locations</SelectItem>
                            {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
                {loading && workers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 space-y-4">
                        <Loader2 size={28} className="animate-spin text-blue-500" />
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Loading Personnel...</p>
                    </div>
                ) : workers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 space-y-4 text-gray-300">
                        <div className="w-20 h-20 bg-gray-50 rounded-[28px] flex items-center justify-center shadow-inner">
                            <Users size={36} strokeWidth={1.5} />
                        </div>
                        <div className="text-center">
                            <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No Workers Found</p>
                            <p className="text-[10px] text-gray-300 font-bold mt-1">Add your first field worker to get started</p>
                        </div>
                    </div>
                ) : (
                    workers.map((worker) => (
                        <div
                            key={worker.id}
                            className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden active:scale-[0.99] transition-all"
                            onClick={() => { setSelectedWorker(worker); setIsViewModalOpen(true); }}
                        >
                            <div className="p-4 space-y-3">
                                {/* Top Row: Avatar + Name + Status */}
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded-[16px] flex items-center justify-center text-gray-500 font-black text-sm overflow-hidden shadow-inner shrink-0">
                                        {worker.profilePhoto ? (
                                            <img src={worker.profilePhoto} alt={worker.name} className="w-full h-full object-cover" />
                                        ) : (
                                            getInitials(worker.name)
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-black text-sm text-gray-900 truncate">{worker.name}</h3>
                                            <Badge className={cn(
                                                "rounded-full px-2 py-0.5 text-[8px] font-black border-0 uppercase tracking-wider shrink-0",
                                                worker.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"
                                            )}>
                                                {worker.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1">
                                                <UserCircle size={10} className="text-gray-300" />
                                                {worker.employeeId}
                                            </span>
                                            {worker.jobTitle && (
                                                <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1">
                                                    <Briefcase size={10} className="text-gray-300" />
                                                    {worker.jobTitle}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Info Row */}
                                <div className="flex items-center gap-4 px-1">
                                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                                        <MapPin size={11} className="text-orange-400" />
                                        <span className="truncate max-w-[100px]">{worker.primaryLocation?.name || 'Unassigned'}</span>
                                    </div>
                                    {worker.phone && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                                            <Phone size={11} className="text-blue-400" />
                                            <span>{worker.phone}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 ml-auto">
                                        <Calendar size={11} className="text-gray-300" />
                                        <span>{toSydneyDate(worker.createdAt)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Bar */}
                            <div className="border-t border-gray-50 px-3 py-2 flex items-center justify-between bg-gray-50/30">
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-blue-600 hover:bg-blue-50 gap-1.5"
                                        onClick={(e) => { e.stopPropagation(); setSelectedWorker(worker); setIsViewModalOpen(true); }}
                                    >
                                        <Eye size={12} />
                                        Profile
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 gap-1.5"
                                        onClick={(e) => { e.stopPropagation(); setSelectedWorker(worker); setIsEditModalOpen(true); }}
                                    >
                                        <Edit size={12} />
                                        Edit
                                    </Button>
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 rounded-lg hover:bg-white"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <MoreVertical size={14} className="text-gray-400" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="rounded-xl border-gray-100 font-bold shadow-2xl">
                                        <DropdownMenuItem onClick={() => { setSelectedWorker(worker); setIsDeleteModalOpen(true); }} className="text-xs text-red-600 rounded-lg cursor-pointer">
                                            <Trash2 size={14} className="mr-2" /> Delete Worker
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-50 flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Users size={18} className="text-gray-400" />
                    Field Workers Management
                </div>
                <Table>
                    <TableHeader className="bg-gray-50/50">
                        <TableRow className="border-b border-gray-50">
                            <TableHead className="font-bold text-gray-400">Worker Details</TableHead>
                            <TableHead className="font-bold text-gray-400">Employee ID</TableHead>
                            <TableHead className="font-bold text-gray-400">Location</TableHead>
                            <TableHead className="font-bold text-gray-400">Recent Issuances</TableHead>
                            <TableHead className="font-bold text-gray-400">Total Value</TableHead>
                            <TableHead className="font-bold text-gray-400">Status</TableHead>
                            <TableHead className="font-bold text-gray-400 text-right pr-6">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && workers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="h-64 text-center">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Loading Workers...</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : workers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="h-64 text-center">
                                    <div className="flex flex-col items-center justify-center space-y-4 text-gray-300">
                                        <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center">
                                            <Users size={32} />
                                        </div>
                                        <p className="text-sm font-bold uppercase tracking-widest">No workers found</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            workers.map((worker) => (
                                <TableRow key={worker.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 group">
                                    <TableCell className="pl-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-500 font-bold text-xs">
                                                {worker.profilePhoto ? (
                                                    <img src={worker.profilePhoto} alt={worker.name} className="w-full h-full object-cover rounded-xl" />
                                                ) : (
                                                    getInitials(worker.name)
                                                )}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="font-bold text-sm text-gray-800 leading-tight">{worker.name}</span>
                                                <span className="text-[10px] text-gray-400 font-medium">Joined {toSydneyDate(worker.createdAt)}</span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
                                            <UserCircle size={14} className="text-gray-300" />
                                            {worker.employeeId}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
                                            <MapPin size={14} className="text-gray-300" />
                                            {worker.primaryLocation?.name || 'No Location'}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="font-bold text-sm text-gray-800">{worker.recentIssuancesCount || 0}</span>
                                            <span className="text-[10px] text-gray-400 font-medium">This month</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="font-bold text-sm text-gray-800">{(worker.totalIssuedValue || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                                            <span className="text-[10px] text-gray-400 font-medium">Total issued</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={cn(
                                            "rounded-lg px-2 py-0.5 text-[10px] font-bold border-0",
                                            worker.isActive ? "bg-black text-white" : "bg-gray-100 text-gray-400"
                                        )}>
                                            {worker.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right pr-6">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                onClick={() => { setSelectedWorker(worker); setIsViewModalOpen(true); }}
                                                title="View Details"
                                            >
                                                <Eye size={14} />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg border-gray-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100 transition-all shadow-sm"
                                                onClick={() => { setSelectedWorker(worker); setIsEditModalOpen(true); }}
                                                title="Edit Details"
                                            >
                                                <Edit size={14} />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg border-gray-100 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-100 transition-all shadow-sm"
                                                title="Send Message"
                                            >
                                                <Send size={14} />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg border-gray-100 hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all shadow-sm"
                                                onClick={() => { setSelectedWorker(worker); setIsDeleteModalOpen(true); }}
                                                title="Delete Worker"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Modals */}
            <AddFieldWorkerModal
                isOpen={isAddModalOpen || isEditModalOpen}
                onClose={() => { setIsAddModalOpen(false); setIsEditModalOpen(false); setSelectedWorker(null); }}
                onRefresh={fetchData}
                worker={selectedWorker}
            />

            {/* View Worker Modal */}
            <ViewWorkerModal
                isOpen={isViewModalOpen}
                onClose={() => { setIsViewModalOpen(false); setSelectedWorker(null); }}
                worker={selectedWorker}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={() => handleDelete(selectedWorker?.id)}
                title="Delete Field Worker"
                message={`Are you sure you want to delete ${selectedWorker?.name}? This action cannot be undone.`}
            />
        </div>
    );
};

// --- View Worker Modal ---
const ViewWorkerModal = ({ isOpen, onClose, worker }: any) => {
    if (!worker) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[32px] p-0 overflow-hidden border-none shadow-2xl max-h-[95vh] flex flex-col [&>button:last-child]:text-white">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 sm:p-8 text-white relative shrink-0">
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-20 -mt-20 blur-3xl opacity-20" />
                    </div>
                    <div className="flex justify-between items-start mb-5 relative z-10">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/10 rounded-[20px] flex items-center justify-center backdrop-blur-sm border border-white/10 text-lg sm:text-xl font-black overflow-hidden">
                            {worker.profilePhoto ? <img src={worker.profilePhoto} className="w-full h-full object-cover" /> : worker.name.split(' ').map((n: any) => n[0]).join('')}
                        </div>
                        <Badge className={cn(
                            "border-0 rounded-full px-3 py-1 font-black text-[9px] tracking-[0.15em] uppercase backdrop-blur-md",
                            worker.isActive ? "bg-green-500/20 text-green-200" : "bg-red-500/20 text-red-200"
                        )}>
                            {worker.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-xl sm:text-2xl font-black mb-1 tracking-tight">{worker.name}</h3>
                        <p className="text-white/50 text-[10px] font-black uppercase tracking-[0.2em]">{worker.jobTitle}</p>
                    </div>
                </div>
                <div className="p-6 sm:p-8 space-y-6 flex-1 overflow-y-auto custom-scrollbar bg-white">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="p-4 sm:p-5 bg-gray-50 rounded-[20px] border border-gray-100/50 flex flex-col items-center text-center shadow-sm">
                            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Employee ID</p>
                            <p className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">{worker.employeeId}</p>
                        </div>
                        <div className="p-4 sm:p-5 bg-gray-50 rounded-[20px] border border-gray-100/50 flex flex-col items-center text-center shadow-sm">
                            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Department</p>
                            <p className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">{worker.department || 'N/A'}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                    <Mail size={14} className="text-blue-500" />
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Email</span>
                            </div>
                            <span className="font-bold text-sm text-gray-900 truncate max-w-[140px] text-right">{worker.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                    <Phone size={14} className="text-green-500" />
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Phone</span>
                            </div>
                            <span className="font-bold text-sm text-gray-900">{worker.phone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                    <MapPin size={14} className="text-orange-500" />
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Location</span>
                            </div>
                            <span className="font-bold text-sm text-gray-900 truncate max-w-[140px] text-right">{worker.primaryLocation?.name || 'Unassigned'}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                    <Calendar size={14} className="text-purple-500" />
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Joined</span>
                            </div>
                             <span className="font-bold text-sm text-gray-900">{toSydneyDate(worker.createdAt) || 'N/A'}</span>
                        </div>
                    </div>

                    {worker.additionalNotes && (
                        <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                            <p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.15em] mb-1">Notes</p>
                            <p className="text-xs text-blue-700 leading-relaxed font-medium">{worker.additionalNotes}</p>
                        </div>
                    )}
                </div>
                <div className="p-6 pt-0 shrink-0 bg-white">
                    <Button onClick={onClose} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-[20px] h-14 font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-900/20 transition-all active:scale-[0.98]">
                        Close Profile
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// --- Delete Confirm Modal ---
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: any) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-[32px] p-6 sm:p-8 border-none shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
                <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-500 mb-2">
                        <Trash2 size={42} strokeWidth={1.5} />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-2 font-bold leading-relaxed px-2 tracking-tight opacity-70">{message}</p>
                    </div>
                </div>
                <div className="flex gap-3 mt-8">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-wider border-gray-100 hover:bg-gray-50 transition-all"
                    >
                        Keep Worker
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className="flex-1 rounded-2xl h-14 bg-red-600 hover:bg-red-700 text-white font-black text-[11px] uppercase tracking-wider shadow-lg shadow-red-600/20 transition-all active:scale-95"
                    >
                        Confirm Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FieldWorkers;
